'use client'

import { useState, useTransition } from 'react'
import { useRouter } from 'next/navigation'
import { safeNextPath } from '@/lib/safe-redirect'
import { authClient } from '@/lib/auth-client'
import { Button, Field, inputClass } from '@/components/ui'
import { t } from '@/messages/sv'

export function SignInForm({ nextPath }: { nextPath?: string }) {
  const router = useRouter()
  const [pending, startTransition] = useTransition()
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(formData: FormData) {
    setError(null)
    setSubmitting(true)

    const email = String(formData.get('email') ?? '').trim()
    const password = String(formData.get('password') ?? '')

    const { error: signInError } = await authClient.signIn.email({ email, password })

    setSubmitting(false)

    if (signInError) {
      /*
       * Deliberately generic: distinguishing "no such account" from "wrong password"
       * would let anyone enumerate who works here.
       */
      setError(t.auth.invalidCredentials)
      return
    }

    // The server decides which portal this person lands in — the client does not
    // know their role, and should not.
    startTransition(() => {
      router.replace(safeNextPath(nextPath) ?? '/')
      router.refresh()
    })
  }

  const busy = submitting || pending

  return (
    <form action={onSubmit} className="flex flex-col gap-4">
      <Field label={t.auth.email} htmlFor="email">
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="username"
          required
          autoFocus
          className={inputClass}
          placeholder="namn@foretag.se"
        />
      </Field>

      <Field label={t.auth.password} htmlFor="password">
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          minLength={8}
          className={inputClass}
          placeholder="••••••••••"
        />
      </Field>

      {error ? (
        <p
          role="alert"
          className="rounded-[var(--radius-control)] bg-[var(--color-danger-soft)] px-3 py-2.5 text-[13px] text-[var(--color-red-deep)]"
        >
          {error}
        </p>
      ) : null}

      <Button type="submit" variant="primary" disabled={busy} className="mt-1 w-full py-2.5">
        {busy ? t.common.loading : t.auth.signIn}
      </Button>
    </form>
  )
}
